import * as React from "react";
import { Button, ButtonProps } from "@/core/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";

interface ButtonWithTooltipProps extends ButtonProps {
  disabledTooltip?: React.ReactNode | string;
}

export const ButtonWithTooltip = React.forwardRef<
  HTMLButtonElement,
  ButtonWithTooltipProps
>(({ disabledTooltip, disabled, id, className, ...buttonProps }, ref) => {
  const generatedId = React.useId();

  if (disabled && disabledTooltip) {
    const buttonId = id ?? generatedId;
    // A disabled button can't take focus, so the wrapper is the focus stop:
    // focusing it opens the tooltip, and it borrows the button's label. The
    // ring is drawn on the button so it keeps the button's shape.
    return (
      <Tooltip>
        <TooltipTrigger
          asDiv
          role="group"
          tabIndex={0}
          aria-labelledby={buttonId}
          className="group inline-flex focus-visible:outline-none"
        >
          <Button
            ref={ref}
            id={buttonId}
            disabled
            className={cn(
              "group-focus-visible:ring-1 group-focus-visible:ring-ring",
              className,
            )}
            {...buttonProps}
          />
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      ref={ref}
      id={id}
      disabled={disabled}
      className={className}
      {...buttonProps}
    />
  );
});

ButtonWithTooltip.displayName = "ButtonWithTooltip";
